import impersonationApi, {
  type ImpersonationStatus
} from "@api/impersonationApi";
import { logger } from "@utils/loggingUtils";
import { useEffect, useState } from "react";

// ---------------------------------------------------------------------------
// Module-level singleton so the status API is called exactly once regardless
// of how many components mount the hook.
// ---------------------------------------------------------------------------
let cachedPromise: Promise<ImpersonationStatus | null> | null = null;
let cachedResult: ImpersonationStatus | null | undefined;

function fetchStatusOnce(): Promise<ImpersonationStatus | null> {
  if (!cachedPromise) {
    cachedPromise = impersonationApi
      .getStatus()
      .then(result => {
        logger.log("Impersonation status response", {
          action: "impersonation_status_check",
          is_impersonating: result?.is_impersonating,
          portal_admin_id: result?.portal_admin_id
        });
        cachedResult = result;
        return result;
      })
      .catch((error: unknown) => {
        const err = error as Record<string, unknown>;
        logger.warn("Failed to check impersonation status", {
          action: "impersonation_status_check",
          error: String(error),
          status: (err?.response as Record<string, unknown>)?.status,
          data: JSON.stringify((err?.response as Record<string, unknown>)?.data)
        });
        cachedResult = null;
        return null;
      });
  }
  return cachedPromise;
}

/**
 * Shared hook that exposes the current impersonation status.
 *
 * The underlying API call (`GET /euka-api/v1/impersonate/status`) is
 * de-duplicated at module level so it only fires once per page load,
 * no matter how many components consume this hook.
 */
export function useImpersonation() {
  const [status, setStatus] = useState<ImpersonationStatus | null>(
    // If the result is already cached (e.g. hook re-mounts), use it
    // synchronously to avoid a flash.
    cachedResult !== undefined ? cachedResult : null
  );

  useEffect(() => {
    // If we already have the cached result, no need to wait for the promise.
    if (cachedResult !== undefined) {
      setStatus(cachedResult);
      return;
    }

    let cancelled = false;
    fetchStatusOnce().then(result => {
      if (!cancelled) setStatus(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    /** Whether the current session is an impersonation session. */
    isImpersonating: status?.is_impersonating ?? false,
    /** The full status object (null while loading or on error). */
    status
  };
}

/** Reset the singleton cache. Intended for tests only. */
export function _resetImpersonationCache() {
  cachedPromise = null;
  cachedResult = undefined;
}
